/**
 * The scope of an appointment-rooted PAC queue, in the three forms it takes.
 *
 * The pre- and post-consultation tabs differ only in which appointment types
 * they root on; everything else about "which appointments are in this queue"
 * is shared here so the two cannot drift apart. The three forms have to agree:
 *
 * 1. `buildAppointmentQueueFilter` — the query's `_filter`: type, live status,
 *    and the date range.
 * 2. `appointmentQueueSubscriptionParams` — the Subscription's criteria. Type
 *    ONLY, deliberately coarser than the query. The matcher cannot evaluate
 *    `_filter`, and a bound in the criteria means the event that moves a row
 *    OUT of scope (a cancellation, a reschedule to next week) never arrives —
 *    the row would go stale, or a patient with an earlier valid consult would
 *    vanish (BAR-2708 review).
 * 3. `isInAppointmentQueueScope` — the query's status and date rules applied
 *    client-side, so what arrives on that coarse criteria is scoped the same
 *    way the server scoped the page.
 *
 * By default a queue takes every live status. `statuses` narrows a tab to just
 * those, which is how cancelled consults reach the no-show tab and no other.
 */

import { Appointment, Meta } from '@medplum/fhirtypes';
import { LATEST_CONSULT_TAG_CODE, LATEST_CONSULT_TAG_SYSTEM } from '@bariendo/lib/constants';
import { isVoidAppointment, ListAppointment } from '@bariendo/lib/pac-list-appointments';
import { dateRangeFilterClauses, isWithinListRange, ListRange } from '@/components/Hub/listRange';

/** Each patient's latest live consult (BAR-2865): one record per patient. */
export const LATEST_CONSULT_TAG_CLAUSE = `_tag eq "${LATEST_CONSULT_TAG_SYSTEM}|${LATEST_CONSULT_TAG_CODE}"`;

export function hasLatestConsultTag(appointment: { meta?: Meta }): boolean {
  return Boolean(
    appointment.meta?.tag?.some(
      (tag) => tag.system === LATEST_CONSULT_TAG_SYSTEM && tag.code === LATEST_CONSULT_TAG_CODE
    )
  );
}

export interface AppointmentQueueScopeOptions {
  statuses?: readonly Appointment['status'][];
}

export function buildAppointmentQueueFilter(
  typeTokens: readonly string[],
  range: ListRange,
  { statuses }: AppointmentQueueScopeOptions = {}
): string {
  // Every type, OR'd inside one group so the status and range bounds apply
  // across all of them rather than binding to the last type in the list.
  const anyType = typeTokens.map((token) => `appointment-type eq "${token}"`).join(' or ');
  const statusClauses = statuses
    ? [`(${statuses.map((status) => `status eq "${status}"`).join(' or ')})`]
    : ['status ne "cancelled"', 'status ne "entered-in-error"'];

  const parts = [`(${anyType})`, ...statusClauses, ...dateRangeFilterClauses(range)];

  return `(${parts.join(' and ')})`;
}

export function appointmentQueueSubscriptionParams(typeTokens: readonly string[]): Record<string, string> {
  return { 'appointment-type': typeTokens.join(',') };
}

export function isInAppointmentQueueScope(
  appointment: ListAppointment,
  range: ListRange,
  { statuses }: AppointmentQueueScopeOptions = {}
): boolean {
  const inStatus = statuses
    ? appointment.status !== undefined && statuses.includes(appointment.status)
    : !isVoidAppointment(appointment);
  return inStatus && isWithinListRange(appointment.start, range);
}
